import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@peacecover/component';
import { Xmark } from '@peacecover/icon';
import useUserQuery from '../hooks/queries/useUserQuery';
import { useGetPaymentList } from '../../domains/payments/ui/hooks/useGetPaymentList';
import { useGetUserIdentities } from '../../domains/user/ui/hooks/useGetUserIdentities';
import { openNewTab } from '../../utils/UIUtils';
import {
  isBillingBuyerInfoComplete,
  loadBillingBuyerInfo
} from '../../utils/billingBuyerInfoStorage';
import BettieAgreementSection from './components/BettieAgreementSection';
import Bubble from '../components/floating/Bubble';
import { BettieGradientColor } from '../../constants/BettieGradientColor';
import { useGetBettieProducts } from '../../domains/bettie/ui/hooks/useGetBettieProducts';

const BettieFirstPurchasePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: user } = useUserQuery();
  const { isLoading: isLoadingUserIdentities } = useGetUserIdentities();
  const { data: paymentList } = useGetPaymentList();
  const { data: products } = useGetBettieProducts({
    category: 'first_time_promotion'
  });

  const eventProduct = products?.[0];
  const amount = (eventProduct?.baseAmount ?? 0) - (eventProduct?.discountAmount ?? 0);

  const handleClick = () => {
    if (!user || isLoadingUserIdentities) return;
    const storedBuyerInfo = loadBillingBuyerInfo();
    const hasIdentity =
      user.identified || isBillingBuyerInfoComplete(storedBuyerInfo);
    if (hasIdentity) {
      openNewTab(
        `/payment/inicis?amount=${amount}&productName=${eventProduct?.name}&productId=${eventProduct?.bettieProductId}`
      );
    } else {
      openNewTab(
        `/payment/buyer-info?amount=${amount}&productName=${eventProduct?.name}&productId=${eventProduct?.bettieProductId}`
      );
    }
  };

  const isDisabled = paymentList && paymentList?.length > 0;

  return (
    <div className="fixed inset-0 flex flex-col items-center bg-[#FFE7E4] min-h-screen pb-[env(safe-area-inset-bottom)]">
      <div className="relative w-full">
        <button
          type="button"
          className="absolute top-[10px] left-[16px]"
          onClick={() => navigate('/bettie-chat-list', { replace: true })}
        >
          <Xmark className="text-pure-white" size={24} />
        </button>
        <img src="/assets/bettie/bettie_purchase_promotion.png" alt="" />
        <div className="bg-[linear-gradient(180deg,rgba(255,231,228,0.00)_0%,#FFE7E4_100%)] h-[134px] absolute bottom-0 left-0 right-0" />
      </div>
      <div className="px-[24px] w-full fixed bottom-[12px]">
        <div className="ml-[20px] mb-[-9px]">
          <Bubble text={t('BettieFirstPurchase.enoughToExperience')} gradientDirection="left" />
        </div>
        <div className="bg-gradient-multi-rainbox-lv2 p-[1px] rounded-[16px] shadow-[0_0_2px_0_rgba(0,0,0,0.05),0_0_8px_0_rgba(0,0,0,0.07)]">
          <div
            className="rounded-[15px] p-[19px] flex items-center justify-between"
            style={BettieGradientColor.bettieProductFill.light}
          >
            <p className="text-[15px] font-semibold leading-[18px] text-text-main-lv2">
              {eventProduct?.name}
            </p>
            <div className="flex flex-col items-end gap-[2px]">
              <p className="text-[15px] font-semibold leading-[18px] text-text-primary">
                {amount?.toLocaleString()}원
              </p>
              <div className="flex items-center gap-[4px]">
                <div className="flex items-center">
                  <p className="text-[12px] font-bold leading-[14px] bg-gradient-single-red bg-clip-text text-transparent">
                    33%
                  </p>
                  <GradientArrowDownIcon />
                </div>
                <p className="text-[12px] font-medium leading-[14px] text-text-sub-lv1 line-through">
                  {Number(eventProduct?.baseAmount)?.toLocaleString()}원
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-[32px] mb-[21px]">
          <BettieAgreementSection />
        </div>
        <p className="text-[12px] font-semibold leading-[14px] text-text-primary text-center">
          {t('BettieFirstPurchase.description')}
        </p>
        <Button
          size="large"
          className="text-[17px] font-semibold w-full mt-[12px] rounded-[18px]"
          disabled={isDisabled}
          onClick={handleClick}
        >
          {isDisabled
            ? t('BettieFirstPurchase.disabledButton')
            : t('BettieFirstPurchase.agreeAndPay')}
        </Button>
      </div>
    </div>
  );
};

export default BettieFirstPurchasePage;

const GradientArrowDownIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="9"
      height="10"
      viewBox="0 0 9 10"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M4.37114e-07 4.83301L2 4.83301L2 -6.11959e-07L7 -1.74846e-07L7 4.83301L9 4.83301L4.5 9.83301L4.37114e-07 4.83301Z"
        fill="url(#paint0_linear_9435_23906)"
      />
      <defs>
        <linearGradient
          id="paint0_linear_9435_23906"
          x1="9"
          y1="9.83301"
          x2="-0.07347"
          y2="9.76474"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FF45A9" />
          <stop offset="1" stopColor="#FF6A6A" />
        </linearGradient>
      </defs>
    </svg>
  );
};
